import React, { useState, useEffect } from 'react';
import { Loader, CheckCircle, TrendingUp, Plus, Clock, Paperclip, Calendar, DollarSign, Edit2, X, Save, Send, AlertCircle, RefreshCw } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { 
  subscribeToProjectMilestones,
  calculateProgress,
  createMilestone,
  updateMilestone,
  deleteMilestone
} from '../../services/milestoneService';
import { subscribeToActiveProject, createProject, updateProject } from '../../services/projectService';
import { 
  notifyMilestoneCreated, 
  notifyMilestoneUpdated, 
  notifyMilestoneDeleted,
  notifyBudgetRequestSubmitted,
  notifyClientProjectCreated
} from '../../services/notificationService';
import MilestoneCard from '../common/MilestoneCard';
import MilestoneForm from '../admin/MilestoneForm';
import ClientProjectForm from './ClientProjectForm';
import FilesModal from '../common/FilesModal';
import BudgetModal from './BudgetModal';
import './ProjectMilestones.css';

const ProjectMilestones = ({ currentUser }) => {
  const [activeProject, setActiveProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectFiles, setProjectFiles] = useState([]);
  const [filesModal, setFilesModal] = useState(null);
  const [showEditProject, setShowEditProject] = useState(false);
  const [editProjectData, setEditProjectData] = useState({});
  const [savingProject, setSavingProject] = useState(false);
  const [budgetRequest, setBudgetRequest] = useState(null);
  const [showBudget, setShowBudget] = useState(false);
  const [canEditBudgets, setCanEditBudgets] = useState(false);

  const loadBudgetRequest = async () => {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'budgetRequests'),
          where('clientId', '==', currentUser.uid)
        )
      );
      const active = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .find(r => ['pending', 'reviewing', 'rejected'].includes(r.status));
      setBudgetRequest(active || null);
    } catch (error) {
      console.error('Budget request error:', error);
    }
  };

  useEffect(() => {
    if (!currentUser?.uid) return;

    const checkPermissions = async () => {
      try {
        const userDoc = await getDocs(
          query(collection(db, 'users'), where('__name__', '==', currentUser.uid))
        );
        if (!userDoc.empty) {
          const userData = userDoc.docs[0].data();
          setCanEdit(userData.permissions?.canEditMilestones || false);
          setCanEditBudgets(userData.permissions?.canEditBudgets || false);
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
      }
    };

    checkPermissions();

    const unsubscribeProject = subscribeToActiveProject(currentUser.uid, (project) => {
      setActiveProject(project);
      setLoading(false);
    });

    loadBudgetRequest();

    return () => unsubscribeProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!activeProject) {
      setMilestones([]);
      setProjectFiles([]);
      return;
    }

    const unsubscribeMilestones = subscribeToProjectMilestones(activeProject.id, (data) => {
      const sorted = data.sort((a, b) => {
        if (a.urgent && !b.urgent) return -1;
        if (!a.urgent && b.urgent) return 1;
        return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
      });
      setMilestones(sorted);
    });

    loadProjectFiles(activeProject.id);

    return () => unsubscribeMilestones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject]);

  const loadProjectFiles = async (projectId) => {
    try {
      const q = query(
        collection(db, 'files'),
        where('projectId', '==', projectId),
        where('clientId', '==', currentUser.uid)
      );
      const snapshot = await getDocs(q);
      setProjectFiles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading files:', error);
    }
  };

  const getMilestoneFiles = (milestoneId) =>
    projectFiles.filter(f => f.milestoneId === milestoneId);

  const getUnassignedFiles = () =>
    projectFiles.filter(f => !f.milestoneId);

  const handleViewFiles = (milestoneId, milestoneTitle) => {
    setFilesModal({ title: `Files — ${milestoneTitle}`, files: getMilestoneFiles(milestoneId) });
  };

  const handleViewProjectFiles = () => {
    setFilesModal({ title: `Project Files — ${activeProject.name}`, files: getUnassignedFiles() });
  };

  const handleOpenEditProject = () => {
    setEditProjectData({
      name: activeProject.name || '',
      description: activeProject.description || '',
      budget: activeProject.budget || '',
      startDate: activeProject.startDate ? formatDateInput(activeProject.startDate) : '',
      endDate: activeProject.endDate ? formatDateInput(activeProject.endDate) : '',
    });
    setShowEditProject(true);
  };

  const handleSaveProject = async () => {
    setSavingProject(true);
    try {
      const updates = {
        name: editProjectData.name,
        description: editProjectData.description,
        budget: editProjectData.budget ? parseFloat(editProjectData.budget) : null,
        startDate: editProjectData.startDate ? new Date(editProjectData.startDate) : null,
        endDate: editProjectData.endDate ? new Date(editProjectData.endDate) : null,
      };
      const result = await updateProject(activeProject.id, updates);
      if (result.success) {
        setShowEditProject(false);
      } else {
        alert('Error saving project: ' + result.error);
      }
    } catch (error) {
      alert('Error saving project: ' + error.message);
    } finally {
      setSavingProject(false);
    }
  };

  const formatDateInput = (timestamp) => {
    if (!timestamp) return '';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toISOString().split('T')[0];
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const handleCreateProject = async (projectData) => {
    if (projectData.isBudgetRequest) {
    if (!projectData.isEdit) {
      await notifyBudgetRequestSubmitted(
        currentUser.uid,
        projectData.name,
        currentUser.displayName || currentUser.email
      );
    }
    setShowProjectForm(false);
    await loadBudgetRequest();
    return;
  }

    if (activeProject) {
      alert('You already have an active project.');
      setShowProjectForm(false);
      return;
    }

    const result = await createProject({
      ...projectData,
      clientId: currentUser.uid,
      clientName: currentUser.displayName || currentUser.email
    });

    if (result.success) {
      setShowProjectForm(false);
      try {
        await notifyClientProjectCreated(
          currentUser.displayName || currentUser.email,
          projectData.name
        );
      } catch(e) { console.error(e); }
      window.location.reload();
    } else {
      alert('Error creating project: ' + result.error);
    }
  };

  const handleCreateMilestone = async (milestoneData) => {
    if (!activeProject) return;
    const result = await createMilestone({
      ...milestoneData,
      projectId: activeProject.id,
      clientId: currentUser.uid,
      clientName: currentUser.displayName || currentUser.email
    });
    if (result.success) {
      setShowForm(false);
      await notifyMilestoneCreated(
        currentUser.uid,
        milestoneData.title,
        activeProject.name,
        currentUser.displayName || currentUser.email
      );
    } else {
      alert('Error creating milestone: ' + result.error);
    }
  };

  const handleUpdateMilestone = async (milestoneData) => {
    const result = await updateMilestone(editingMilestone.id, milestoneData);
    if (result.success) {
      setShowForm(false);
      setEditingMilestone(null);
      await notifyMilestoneUpdated(
        currentUser.uid,
        milestoneData.title,
        currentUser.displayName || currentUser.email
      );
    } else {
      alert('Error updating milestone: ' + result.error);
    }
  };

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone);
    setShowForm(true);
  };

  const handleDelete = async (milestoneId) => {
    if (!window.confirm('Are you sure you want to delete this milestone?')) return;
    const milestoneToDelete = milestones.find(m => m.id === milestoneId);
    const result = await deleteMilestone(milestoneId);
    if (result.success) {
      await notifyMilestoneDeleted(
        currentUser.uid,
        milestoneToDelete?.title || 'Unknown',
        currentUser.displayName || currentUser.email
      );
      if (activeProject) loadProjectFiles(activeProject.id);
    } else {
      alert('Error deleting milestone: ' + result.error);
    }
  };

  const progress = calculateProgress(milestones);
  const completedCount = milestones.filter(m => m.status === 'completed').length;
  const unassignedFiles = getUnassignedFiles();

  const initialFormData = editingMilestone || (activeProject ? {
    clientId: currentUser.uid,
    clientName: currentUser.displayName || currentUser.email,
    projectId: activeProject.id
  } : {});

  const renderMilestoneGroup = (filteredMilestones) =>
    filteredMilestones.map((milestone, index, arr) => (
      <div key={milestone.id} className="timeline-item">
        <div className="timeline-connector">
          <div className={`timeline-dot ${milestone.status}`} />
          {index < arr.length - 1 && <div className="timeline-line" />}
        </div>
        <div className="timeline-content">
          <MilestoneCard
            milestone={milestone}
            onEdit={canEdit ? handleEdit : null}
            onDelete={canEdit ? handleDelete : null}
            onUpdateStatus={null}
            isAdmin={canEdit}
            files={getMilestoneFiles(milestone.id)}
            onViewFiles={handleViewFiles}
          />
        </div>
      </div>
    ));

  const renderBudgetRequest = () => {
    if (!budgetRequest) return null;

    const statusConfig = {
      pending: {
        color: '#f59e0b',
        bg: '#fffbeb',
        border: '#fde68a',
        icon: <Clock size={20} />,
        label: 'Waiting for review',
        desc: 'Your budget request has been received and is waiting to be reviewed.'
      },
      reviewing: {
        color: '#3b82f6',
        bg: '#eff6ff',
        border: '#bfdbfe',
        icon: <RefreshCw size={20} />,
        label: 'Under review',
        desc: 'We are reviewing your request. We will get back to you soon.'
      },
      rejected: {
        color: '#ef4444',
        bg: '#fef2f2',
        border: '#fecaca',
        icon: <AlertCircle size={20} />,
        label: 'Request not approved',
        desc: budgetRequest.rejectionReason || 'Your request was not approved at this time.'
      }
    };

    const cfg = statusConfig[budgetRequest.status];

    return (
      <div className="budget-request-card" style={{ borderColor: cfg.border, background: cfg.bg }}>
        <div className="budget-request-header" style={{ color: cfg.color }}>
          {cfg.icon}
          <span>{cfg.label}</span>
        </div>
        <h3 className="budget-request-name">{budgetRequest.name}</h3>
        {budgetRequest.description && (
          <p className="budget-request-desc">{budgetRequest.description}</p>
        )}
        <div className="budget-request-meta">
          {budgetRequest.budget && (
            <span>Budget: {formatCurrency(budgetRequest.budget)}</span>
          )}
          {budgetRequest.deadline && (
            <span>Deadline: {formatDate(budgetRequest.deadline)}</span>
          )}
        </div>
        <p className="budget-request-status-desc">{cfg.desc}</p>
        <div className="budget-request-actions">
          {budgetRequest.status === 'pending' && (
            <>
              <button className="btn-edit-request" onClick={() => setShowProjectForm(true)}>
                <Edit2 size={15} />
                Edit Request
              </button>
              <button
                className="btn-cancel-request"
                onClick={async () => {
                  if (!window.confirm('Cancel this budget request?')) return;
                  const { doc, updateDoc } = await import('firebase/firestore');
                  await updateDoc(doc(db, 'budgetRequests', budgetRequest.id), { status: 'cancelled' });
                  await loadBudgetRequest();
                }}
              >
                <X size={15} />
                Cancel
              </button>
            </>
          )}
          {budgetRequest.status === 'rejected' && (
            <button className="btn-new-request" onClick={() => setShowProjectForm(true)}>
              <Send size={15} />
              Submit New Request
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="project-milestones-loading">
        <Loader className="spinner" size={32} />
        <p>Loading your project...</p>
      </div>
    );
  }

  if (!activeProject) {
    return (
      <div className="project-milestones">
        <div className="project-header">
          <div>
            <h1>Your Current Project</h1>
            <p>Track the progress of your project milestones</p>
          </div>
        </div>

        {budgetRequest ? (
          renderBudgetRequest()
        ) : (
          <div className="no-active-project">
            <p>You don't have an active project at the moment</p>
            {canEdit ? (
              <button onClick={() => setShowProjectForm(true)} className="btn-new-project">
                <Plus size={18} />
                Create New Project
              </button>
            ) : (
              <button onClick={() => setShowProjectForm(true)} className="btn-request-budget">
                <Send size={18} />
                Request a Budget
              </button>
            )}
          </div>
        )}

        {showProjectForm && (
          <ClientProjectForm
            isBudgetRequest={!canEdit}
            currentUser={currentUser}
            initialData={budgetRequest}
            onSave={handleCreateProject}
            onCancel={() => setShowProjectForm(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="project-milestones">
      <div className="project-header">
        <div>
          <h1>Your Current Project</h1>
          <p>Track the progress of your project milestones</p>
        </div>
        {canEdit && (
          <button onClick={() => setShowForm(true)} className="btn-create">
            <Plus size={20} />
            New Milestone
          </button>
        )}
      </div>

      <div className="project-info-card">
        <div className="project-info-header">
          <div>
            <h2 className="project-info-name">{activeProject.name}</h2>
            {activeProject.description && (
              <p className="project-info-desc">{activeProject.description}</p>
            )}
          </div>
          <div style={{display:'flex',gap:'8px'}}>
            <button className="btn-budget-label" onClick={() => setShowBudget(true)}>
              Budget
            </button>
            {canEdit && (
              <button className="btn-edit-project" onClick={handleOpenEditProject}>
                <Edit2 size={16} />
                Edit
              </button>
            )}
          </div>
        </div>
        <div className="project-info-meta">
          {activeProject.budget && (
            <span className="project-meta-item">
              <DollarSign size={14} />
              {formatCurrency(activeProject.budget)}
            </span>
          )}
          {activeProject.startDate && (
            <span className="project-meta-item">
              <Calendar size={14} />
              Start: {formatDate(activeProject.startDate)}
            </span>
          )}
          {activeProject.endDate && (
            <span className="project-meta-item">
              <Calendar size={14} />
              Est. End: {formatDate(activeProject.endDate)}
            </span>
          )}
        </div>
      </div>

      <div className="project-progress-card">
        <div className="progress-header">
          <div className="progress-info">
            <TrendingUp size={24} className="progress-icon" />
            <div>
              <h3>Overall Progress</h3>
              <p>{completedCount} of {milestones.length} milestones completed</p>
            </div>
          </div>
          <div className="progress-percentage">{progress}%</div>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
        {progress === 100 && (
          <div className="project-completed">
            <CheckCircle size={20} />
            <span>Project completed! 🎉</span>
          </div>
        )}
      </div>

      {unassignedFiles.length > 0 && (
        <div className="project-files-bar">
          <span>Files not assigned to a milestone ({unassignedFiles.length})</span>
          <button className="btn-view-project-files" onClick={handleViewProjectFiles}>
            <Paperclip size={16} />
            View Files ({unassignedFiles.length})
          </button>
        </div>
      )}

      <div className="project-timeline">
        {milestones.length === 0 ? (
          <div className="timeline-empty">
            <p>No milestones yet</p>
            {canEdit ? (
              <button onClick={() => setShowForm(true)} className="btn-create-empty">
                <Plus size={20} />
                Create first milestone
              </button>
            ) : (
              <p className="timeline-empty-subtitle">Your designer will create project milestones soon</p>
            )}
          </div>
        ) : (
          <div className="timeline-grouped">
            {milestones.filter(m => m.status === 'in_progress').length > 0 && (
              <div className="milestone-group">
                <h3 className="group-title in-progress">
                  <Clock size={18} />
                  Milestones In Progress ({milestones.filter(m => m.status === 'in_progress').length})
                </h3>
                <div className="timeline-list">
                  {renderMilestoneGroup(milestones.filter(m => m.status === 'in_progress'))}
                </div>
              </div>
            )}
            {milestones.filter(m => m.status === 'pending').length > 0 && (
              <div className="milestone-group">
                <h3 className="group-title pending">
                  <CheckCircle size={18} />
                  Milestones Pending ({milestones.filter(m => m.status === 'pending').length})
                </h3>
                <div className="timeline-list">
                  {renderMilestoneGroup(milestones.filter(m => m.status === 'pending'))}
                </div>
              </div>
            )}
            {milestones.filter(m => m.status === 'completed').length > 0 && (
              <div className="milestone-group">
                <h3 className="group-title completed">
                  <CheckCircle size={18} />
                  Milestones Completed ({milestones.filter(m => m.status === 'completed').length})
                </h3>
                <div className="timeline-list">
                  {renderMilestoneGroup(milestones.filter(m => m.status === 'completed'))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showEditProject && (
        <div className="edit-project-overlay" onClick={() => setShowEditProject(false)}>
          <div className="edit-project-modal" onClick={e => e.stopPropagation()}>
            <div className="edit-project-header">
              <h3>Edit Project</h3>
              <button className="edit-project-close" onClick={() => setShowEditProject(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="edit-project-body">
              <div className="edit-field">
                <label>Project Name</label>
                <input
                  type="text"
                  value={editProjectData.name}
                  onChange={e => setEditProjectData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Project name"
                />
              </div>
              <div className="edit-field">
                <label>Description</label>
                <textarea
                  value={editProjectData.description}
                  onChange={e => setEditProjectData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Project description"
                  rows={3}
                />
              </div>
              <div className="edit-field">
                <label>Budget (€)</label>
                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  <input
                    type="number"
                    value={editProjectData.budget}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    readOnly
                    style={{background:'#f9fafb',color:'#6b7280',cursor:'not-allowed',flex:1}}
                  />
                  <button
                    type="button"
                    onClick={() => { setShowEditProject(false); setShowBudget(true); }}
                    style={{whiteSpace:'nowrap',padding:'6px 12px',background:'#dcfce7',color:'#16a34a',border:'1px solid #bbf7d0',borderRadius:'6px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                  >
                    Budget
                  </button>
                </div>
              </div>
              <div className="edit-field-row">
                <div className="edit-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={editProjectData.startDate}
                    onChange={e => setEditProjectData(p => ({ ...p, startDate: e.target.value }))}
                  />
                </div>
                <div className="edit-field">
                  <label>Est. End Date</label>
                  <input
                    type="date"
                    value={editProjectData.endDate}
                    onChange={e => setEditProjectData(p => ({ ...p, endDate: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="edit-project-footer">
              <button className="btn-cancel-edit" onClick={() => setShowEditProject(false)}>
                Cancel
              </button>
              <button className="btn-save-edit" onClick={handleSaveProject} disabled={savingProject}>
                {savingProject ? <Loader size={16} className="spinning" /> : <Save size={16} />}
                {savingProject ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <MilestoneForm
          milestone={initialFormData}
          clients={[]}
          projects={[]}
          isAdmin={false}
          onSave={editingMilestone ? handleUpdateMilestone : handleCreateMilestone}
          onCancel={() => {
            setShowForm(false);
            setEditingMilestone(null);
          }}
        />
      )}

      {filesModal && (
        <FilesModal
          title={filesModal.title}
          files={filesModal.files}
          onClose={() => setFilesModal(null)}
        />
      )}

      {showBudget && activeProject && (
        <BudgetModal
          project={activeProject}
          milestones={milestones}
          currentUser={{ ...currentUser, permissions: { canEditBudgets: canEditBudgets } }}
          isAdmin={false}
          onClose={() => setShowBudget(false)}
        />
      )}
    </div>
  );
};

export default ProjectMilestones;